import { useState, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { Category } from '../../data/products';
import {
  Plus,
  Edit,
  Trash2,
  Tag,
  X,
  Save,
  Palette,
} from 'lucide-react';

const COLOR_OPTIONS = [
  '#E31E24', '#FF4500', '#FF6B00', '#F59E0B',
  '#10B981', '#06B6D4', '#0EA5E9', '#6366F1',
  '#8B5CF6', '#EC4899', '#EF4444', '#64748B',
];

export default function AdminCategories() {
  const { categories, products, addCategory, updateCategory, deleteCategory, fetchCategoriesFromAPI } = useAdminStore();

  useEffect(() => {
    fetchCategoriesFromAPI();
  }, []);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    color: '#E31E24',
    icon: 'spray',
  });

  const getProductCount = (categoryId: string) =>
    products.filter((p) => p.categories.includes(categoryId)).length;

  const openAddForm = () => {
    setEditingCategory(null);
    setFormData({ name: '', slug: '', color: '#E31E24', icon: 'spray' });
    setShowForm(true);
  };

  const openEditForm = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      color: category.color,
      icon: category.icon,
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (editingCategory) {
      updateCategory(editingCategory.id, formData);
    } else {
      addCategory({
        ...formData,
        id: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
      });
    }
    setShowForm(false);
    setEditingCategory(null);
  };

  const handleDelete = (id: string) => {
    const count = getProductCount(id);
    if (count > 0) {
      alert(`Bu kategoride ${count} ürün bulunmaktadır. Önce ürünleri silin veya başka kategoriye taşıyın.`);
      return;
    }
    deleteCategory(id);
    setDeleteConfirm(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kategori Yönetimi</h1>
          <p className="text-gray-500 mt-1">{categories.length} kategori mevcut</p>
        </div>
        <button
          onClick={openAddForm}
          className="flex items-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
        >
          <Plus className="w-5 h-5" />
          Yeni Kategori
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <Tag className="w-6 h-6" style={{ color: category.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-500">{getProductCount(category.id)} ürün</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEditForm(category)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-blue-600 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(category.id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <span className="text-xs text-gray-400">Renk:</span>
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: category.color }}
              />
              <span className="text-xs font-mono text-gray-500">{category.color}</span>
            </div>

            {/* Delete Confirmation */}
            {deleteConfirm === category.id && (
              <div className="mt-4 p-3 bg-red-50 rounded-xl">
                <p className="text-sm text-red-700 mb-2">Bu kategoriyi silmek istediğinize emin misiniz?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="flex-1 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                  >
                    Sil
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300"
                  >
                    İptal
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Category Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingCategory(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Kategori Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  placeholder="Örn: Oto Bakım"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">URL Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  placeholder="oto-bakim"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Renk</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-xl transition-all ${
                        formData.color === color
                          ? 'ring-2 ring-offset-2 ring-brand-red scale-110'
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">İkon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                >
                  <option value="spray">Spray (Aerosol)</option>
                  <option value="cleaning">Temizlik</option>
                  <option value="perfume">Parfüm</option>
                  <option value="water">Su</option>
                  <option value="winter">Kış</option>
                  <option value="antifreeze">Antifriz</option>
                  <option value="carmaintenance">Araç Bakım</option>
                  <option value="professional">Professional</option>
                  <option value="customset">Özel Set</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => { setShowForm(false); setEditingCategory(null); }}
                className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingCategory ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
